import type { CSSProperties, HTMLAttributes } from "react";
import { cx } from "../../utils/cx";
import styles from "./HtmlPreview.module.scss";

/** Scrollbars only — safe to inject into full HTML email documents. */
export const HTML_PREVIEW_SCROLLBAR_STYLE = `<style id="ds-scrollbar">
html, body { scrollbar-width: thin; scrollbar-color: #bfbfbf transparent; }
html::-webkit-scrollbar, body::-webkit-scrollbar { width: 6px; height: 6px; }
html::-webkit-scrollbar-track, body::-webkit-scrollbar-track { background: transparent; }
html::-webkit-scrollbar-thumb, body::-webkit-scrollbar-thumb {
  background: #bfbfbf; border-radius: 999px;
}
html::-webkit-scrollbar-thumb:hover, body::-webkit-scrollbar-thumb:hover {
  background: #8c8c8c;
}
html::-webkit-scrollbar-button, body::-webkit-scrollbar-button {
  display: none; width: 0; height: 0;
}
</style>`;

/** Base + inset past the floating label (plain / fragment previews). */
export const HTML_PREVIEW_BASE_STYLE = `<style id="ds-html-preview-base">
html, body {
  margin: 0;
  padding: 28px 16px 16px;
  background: transparent;
  color: #0f1709;
  font-size: 15px;
  line-height: 24px;
  scrollbar-width: thin;
  scrollbar-color: #bfbfbf transparent;
}
html::-webkit-scrollbar, body::-webkit-scrollbar { width: 6px; height: 6px; }
html::-webkit-scrollbar-track, body::-webkit-scrollbar-track { background: transparent; }
html::-webkit-scrollbar-thumb, body::-webkit-scrollbar-thumb {
  background: #bfbfbf; border-radius: 999px;
}
html::-webkit-scrollbar-thumb:hover, body::-webkit-scrollbar-thumb:hover {
  background: #8c8c8c;
}
html::-webkit-scrollbar-button, body::-webkit-scrollbar-button {
  display: none; width: 0; height: 0;
}
p { margin: 0 0 0.75em; }
p:last-child { margin-bottom: 0; }
</style>`;

const DEFAULT_EMPTY_MESSAGE = "There is no template available to view...";

const escapeHtml = (value: string) =>
    value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");

const looksLikeHtml = (value: string) => /<[a-z][\s\S]*>/i.test(value);

const looksLikeFullDocument = (value: string) =>
    /<!doctype html|<html[\s>]|<body[\s>]/i.test(value);

const wrapPlainText = (value: string) =>
    `<p style="margin:0;white-space:pre-wrap;word-break:break-word;">${escapeHtml(
        value,
    )}</p>`;

export function buildHtmlPreviewSrcDoc(
    html?: string | null,
    emptyMessage: string = DEFAULT_EMPTY_MESSAGE,
): string {
    const trimmed = html?.trim();
    if (trimmed) {
        if (looksLikeFullDocument(trimmed)) {
            return `${HTML_PREVIEW_SCROLLBAR_STYLE}${trimmed}`;
        }
        const body = looksLikeHtml(trimmed) ? trimmed : wrapPlainText(trimmed);
        return `${HTML_PREVIEW_BASE_STYLE}${body}`;
    }

    return `${HTML_PREVIEW_SCROLLBAR_STYLE}
<style>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;700&display=swap');
p {
  color: #9CA1B4;
  font-size: 14px;
  line-height: 1.5;
  padding-left: 14px;
  font-family: 'Poppins', sans-serif;
}
</style>
<p>${escapeHtml(emptyMessage)}</p>`;
}

export interface HtmlPreviewProps
    extends Omit<HTMLAttributes<HTMLDivElement>, "children"> {
    /** HTML (or plain text) rendered inside the iframe. */
    html?: string | null;
    /** Floating label inside the shell (default `Preview`). */
    label?: string;
    /** iframe title for a11y. */
    title?: string;
    /**
     * Fixed frame height in px. Omit to fill the parent (e.g. match
     * Warnings Textarea height in a FormLayout grid).
     */
    height?: number;
    /** Empty-state copy when `html` is blank. */
    emptyMessage?: string;
}

/**
 * Treez DS — HTML preview shell (matches filled Textarea look).
 * Shared by Checkout Warnings and Notification Setting email preview.
 */
export function HtmlPreview({
    html,
    label = "Preview",
    title = "Preview",
    height,
    emptyMessage = DEFAULT_EMPTY_MESSAGE,
    className,
    style,
    ...rest
}: HtmlPreviewProps) {
    const frameStyle = {
        ...style,
        ...(height != null
            ? { ["--html-preview-height" as string]: `${height}px` }
            : null),
    } as CSSProperties;

    return (
        <div
            className={cx(styles.root, className)}
            data-ui="html-preview"
            style={frameStyle}
            {...rest}
        >
            <span className={styles.label}>{label}</span>
            <iframe
                className={styles.frame}
                srcDoc={buildHtmlPreviewSrcDoc(html, emptyMessage)}
                title={title}
            />
        </div>
    );
}

HtmlPreview.displayName = "HtmlPreview";
